/**
 * The course bridge (protocol v1): a SCORM course runs in an iframe inside the task, and the player in it
 * talks only to the web app. The app holds the login and loads/saves the progress itself, so the course
 * url carries no token, task ids or names. The contract is defined in the courses repo
 * (.claude/skills/scorm-player/SKILL.md, "The bridge protocol (v1)").
 */
export const COURSE_BRIDGE_VERSION = 1
const COURSE_SOURCE = 'storee-course'
const APP_SOURCE = 'storee-app'

export type CourseBridgeError = 'unauthorized' | 'forbidden' | 'invalid' | 'server' | 'network'

export type CourseMessageType = 'ready' | 'load' | 'save' | 'finished' | 'flush-result' | 'close'
const COURSE_MESSAGE_TYPES: CourseMessageType[] = ['ready', 'load', 'save', 'finished', 'flush-result', 'close']

export interface ICourseMessage {
  type: CourseMessageType
  id?: string
  replyTo?: string
  payload?: Record<string, any>
}

export type AppMessageType = 'init' | 'init-error' | 'load-result' | 'save-result' | 'flush'

/** The origin the course is served from, or null for a link that is not a valid url. */
export const courseOrigin = (link?: string | null): string | null => {
  try {
    return link ? new URL(link).origin : null
  } catch {
    return null
  }
}

/** The course page itself: the link without any query or hash (nothing about the user travels in it). */
export const courseViewerUrl = (link: string) => {
  const url = new URL(link)
  url.search = ''
  url.hash = ''
  return url.toString()
}

/**
 * A message from our course frame, or null for anything else: another window, another origin (only the
 * course's own origin may talk to the app), another tool's message or a malformed one.
 */
export function parseCourseMessage(
  event: Pick<MessageEvent, 'data' | 'origin' | 'source'>,
  origin: string | null,
  frame: Window | null | undefined
): ICourseMessage | null {
  if (!origin || !frame || event.source !== frame || event.origin !== origin) return null

  const data = event.data
  if (!data || typeof data !== 'object' || data.source !== COURSE_SOURCE || data.v !== COURSE_BRIDGE_VERSION)
    return null
  if (COURSE_MESSAGE_TYPES.indexOf(data.type) === -1) return null
  if (data.id !== undefined && typeof data.id !== 'string') return null

  return { type: data.type, id: data.id, replyTo: data.replyTo, payload: data.payload || {} }
}

export const appMessage = (type: AppMessageType, extra: { id?: string; replyTo?: string; payload?: unknown } = {}) => ({
  source: APP_SOURCE,
  v: COURSE_BRIDGE_VERSION,
  type,
  ...extra,
})

/** An API failure as the player understands it (it shows the matching screen). */
export function toBridgeError(error: any): { error: CourseBridgeError; status?: number } {
  const status: number | undefined = error?.response?.status
  if (!status) return { error: 'network' }
  if (status === 401) return { error: 'unauthorized', status }
  if (status === 403) return { error: 'forbidden', status }
  if (status >= 400 && status < 500) return { error: 'invalid', status }
  return { error: 'server', status }
}

/** Names the player's local backup of unsaved progress: one per learner, sub-task, context and occurrence. */
export const courseStorageKey = (stId: string, contextId: string, occurrence: string | null, uid: string) =>
  [stId, contextId, occurrence ?? '', uid].join(':')
